import { useEffect, useState } from "react";
import { Trophy } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Panel } from "@/components/PageHeader";
import { api, eur, num, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FilterContext";

const COLS = [["spend", "Vendite", eur], ["growth", "vs a.p.", null], ["receipts", "Scontrini", num], ["avg_receipt", "Scontrino medio", eur],
  ["clients", "Clienti fed.", num], ["fidelity_share", "% fedeltà", (v) => `${(v ?? 0).toLocaleString("it-IT")}%`], ["points", "Punti", num]];
const pctTxt = (v) => (v === null || v === undefined ? null : `${v > 0 ? "+" : ""}${v.toFixed(1).replace(".", ",")}%`);

export const useShowCompare = () => {
  const { user } = useAuth();
  const { filters } = useFilters();
  return (user?.allowed_stores?.length || 0) > 1 || (filters.stores?.length || 0) > 1;
};

export function StoreCompare({ months }) {
  const show = useShowCompare();
  const { filters } = useFilters();
  const fkey = JSON.stringify(filters);
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [sort, setSort] = useState("spend");
  useEffect(() => {
    if (!show) return;
    setD(null); setError("");
    api.get("/stores/compare", { params: { months } }).then((r) => setD(r.data)).catch((e) => setError(errMsg(e)));
  }, [months, show, fkey]);
  if (!show) return null;

  const rows = [...(d?.rows || [])].sort((a, b) => (b[sort] ?? -Infinity) - (a[sort] ?? -Infinity));
  const ext = {};
  if (rows.length > 1) COLS.forEach(([k]) => {
    const vals = rows.map((r) => r[k]).filter((v) => v !== null && v !== undefined);
    if (vals.length > 1) ext[k] = { max: Math.max(...vals), min: Math.min(...vals) };
  });
  const cls = (k, v) => {
    const e = ext[k];
    if (!e || v === null || v === undefined || e.max === e.min) return "";
    return v === e.max ? "bg-emerald-50 font-semibold text-emerald-700" : v === e.min ? "bg-rose-50 text-rose-700" : "";
  };
  const cell = (k, fmt, v) => (k === "growth" ? (pctTxt(v) ?? <span className="text-slate-300">—</span>) : fmt(v));

  return (
    <Panel title="Confronto dei miei punti vendita" testId="store-compare" className="mt-6"
      action={d && <span className="text-xs text-slate-500" data-testid="store-compare-count">{d.stores} negozi · migliore in verde, ultimo in rosso</span>}>
      {error && <div className="m-4 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700" data-testid="store-compare-error">{error}</div>}
      {!d && !error && <Skeleton className="m-4 h-40" />}
      {d && rows.length < 2 && <div className="p-5 text-sm text-slate-500" data-testid="store-compare-empty">Servono almeno due punti vendita con vendite nel periodo per il confronto.</div>}
      {d && rows.length >= 2 && (
        <div className="overflow-auto">
          <table className="w-full text-[13px]" data-testid="store-compare-table">
            <thead className="text-left text-slate-500">
              <tr><th className="px-4 py-2 font-medium">Punto vendita</th>
                {COLS.map(([k, l]) => <th key={k} className="cursor-pointer whitespace-nowrap px-3 py-2 text-right font-medium" data-testid={`store-compare-sort-${k}`} onClick={() => setSort(k)}><span className={sort === k ? "text-blue-600" : ""}>{l}</span></th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`store-compare-row-${r.id}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2 font-medium text-slate-800">{i === 0 && sort === "spend" && <Trophy className="h-3.5 w-3.5 text-amber-500" />}<span className="whitespace-normal leading-tight">{r.name}</span></div>
                    <div className="mt-1 flex items-center gap-2"><div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-blue-500" style={{ width: `${Math.min(r.share, 100)}%` }} /></div><span className="font-mono text-[11px] text-slate-500" data-testid={`store-compare-share-${r.id}`}>{(r.share ?? 0).toLocaleString("it-IT")}%</span></div>
                  </td>
                  {COLS.map(([k, , fmt]) => <td key={k} className={`whitespace-nowrap px-3 py-2 text-right font-mono text-xs ${cls(k, r[k])}`} data-testid={`store-compare-${k}-${r.id}`}>{cell(k, fmt, r[k])}</td>)}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50 text-slate-700" data-testid="store-compare-totals">
                <td className="px-4 py-2 font-medium">Totale {d.stores} negozi</td>
                {COLS.map(([k, , fmt]) => <td key={k} className="whitespace-nowrap px-3 py-2 text-right font-mono text-xs font-semibold">{k === "growth" ? "" : fmt(d.totals[k])}</td>)}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </Panel>
  );
}
